import styled from "@emotion/styled/macro";
import { useEffect } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Trans } from "react-i18next";
import { Formik, Form } from "formik";
import { format } from "date-fns";
import { useState } from "react";
import Insured from "./Insured/Insured";
import Unknown from "./Unknown/Unkown";
import SubmitButton from "../../../components/ui/Buttons";
import Captcha from "../../../components/ui/Captcha";
import apiService from "../../../services/ApiService";
import {
  Checkbox,
  ConsentCheckbox,
  DateInputField,
  LegitimateInterestField,
  LicensePlateInputField,
  VinInputField,
  validationSchema,
} from "./helpers";

const Wrapper = styled.div`
  margin: 4em 0;
  background: ${({ theme }) => theme.colors.white};
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 3em;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 2em 1.5em;
  }
  justify-content: space-between;
  display: flex;
  flex-direction: column;
  box-shadow: 0px 0px 19px -11px ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.white};
`;

const TextWrapper = styled.div`
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    line-height: 2.5em;
  }
  max-width: 90%;
`;
const Title = styled.p`
  line-height: 1em;
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: ${({ theme }) => theme.fontSizes.lg};
  color: ${({ theme }) => theme.colors.primary};
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes.xl};
  }
`;
const Subtitle = styled.p`
  margin-top: 1em;
  line-height: 1em;
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: ${({ theme }) => theme.colors.secondary};
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes.lg};
  }
`;

const Error = styled.div`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: ${({ theme }) => theme.colors.red};
  padding-top: 1em;
`;

const Insurance = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const history = useHistory();
  const enableCaptcha = window._env_.REACT_APP_RECAPTCHA_ENABLED;
  const [enteredValues, setEnteredValues] = useState();
  const [isVin, setVin] = useState();
  const [isPlate, setPlate] = useState(true);
  const [isDate, setDate] = useState();
  const [isError, setError] = useState(false);
  const [isInsured, setInsured] = useState(false);
  const [result, setResult] = useState({});
  const [isUnknown, setUnknown] = useState(false);

  useEffect(() => {
    setEnteredValues(location?.state);
    if (enteredValues && enteredValues?.vin !== "" && enteredValues?.licensePlate === "") {
      setVin(true);
      setPlate(false);
    }
    if (enteredValues && enteredValues?.licensePlate !== "" && enteredValues?.vin === "") {
      setPlate(true);
      setVin(false);
    } // eslint-disable-next-line
  }, [enteredValues]);

  const toggleVinAndPlate = () => {
    setVin(!isVin);
    setPlate(!isPlate);
  };
  const toggleDate = () => {
    setDate(!isDate);
  };

  useEffect(() => {
    window.scroll(0, 0);
  }, []);

  const insuredView = () => {
    setInsured(!isInsured);
  };
  const unknownView = () => {
    setUnknown(!isUnknown);
  };
  const backView = () => {
    setUnknown(false);
    setInsured(false);
  };
  const initialValues = {
    licensePlate: "",
    date: new Date(),
    vin: "",
    terms: "",
    info: "",
    legitimateInterest: "",
  };

  const handleView = (status) => {
    setError(false);
    if (status === "INSURED") {
      insuredView();
    } else {
      unknownView();
    }
  };

  const handleSubmit = async (values) => {
    setError(false);
    try {
      const result = await apiService.getBooleanCheck({
        ...values,
        date: format(values.date, "yyyy-MM-dd"),
        licensePlate: values.licensePlate.toUpperCase(),
        vin: values.vin.toUpperCase(),
      });
      setError(false);
      setResult(result);
      handleView(result.status);
      history.replace({});
    } catch (e) {
      setError(true);
    }
  };

  return (
    <>
      {!isInsured && !isUnknown && (
        <Wrapper>
          <TextWrapper>
            <Title>{t("insuranceCheck.title")}</Title>
            <Subtitle>{t("insuranceCheck.subtitle")}</Subtitle>
          </TextWrapper>
          <Formik initialValues={initialValues} validationSchema={validationSchema(t)} onSubmit={handleSubmit}>
            {({ values, errors }) => (
              <Form autoComplete="off">
                <LicensePlateInputField
                  togglePlate={isPlate}
                  values={values}
                  toggleVinAndPlate={toggleVinAndPlate}
                  errors={errors}
                />
                <VinInputField isVin={isVin} values={values} toggleVinAndPlate={toggleVinAndPlate} />
                <DateInputField isDate={isDate} toggleDate={toggleDate} />

                <LegitimateInterestField />

                <Checkbox name="info" error={errors?.info}>
                  <Trans i18nKey="insuranceCheck.checkInfo" />
                </Checkbox>
                <ConsentCheckbox values={values} errors={errors} />

                {enableCaptcha === "true" && <Captcha />}
                {isError && <Error>{t("common.error")}</Error>}

                <SubmitButton>{t("common.check")}</SubmitButton>
              </Form>
            )}
          </Formik>
        </Wrapper>
      )}
      {isInsured && <Insured info={result} back={() => backView()} />}
      {isUnknown && <Unknown info={result} back={() => backView()} />}
    </>
  );
};

export default Insurance;
